// ABOUTME: Keeps the UI preferences store in step with users/{uid}/settings/prefs.
// ABOUTME: Remote snapshots flow into the store; local changes are diffed and written back.

import { useEffect } from "react";
import { useUIStore } from "../stores/ui";
import { prefsService } from "../services/prefsService";
import { diffPrefs, pickPrefs, sanitizePrefs, type UserPrefs } from "../utils/prefs";

// The color picker fires on every pixel of a drag; coalesce before writing.
const WRITE_DEBOUNCE_MS = 400;

/**
 * Mount once per signed-in session (AuthenticatedApp does). Guests never reach
 * this, so for them the store stays a plain localStorage preference.
 *
 * Echo safety: `remote` tracks the preferences as Firestore last reported them,
 * updated *before* a snapshot is applied to the store. The store listener
 * then sees no difference and writes nothing back, so a snapshot can never
 * bounce. A rejected write surfaces as a snapshot with the old value, which
 * flows into the store the same way and reverts the control.
 */
export function usePrefsSync(userId: string | undefined): void {
  useEffect(() => {
    if (!userId) return;

    let remote: UserPrefs | null = null;
    let pending: Partial<UserPrefs> = {};
    let timer: ReturnType<typeof setTimeout> | null = null;

    const flush = () => {
      timer = null;
      const patch = pending;
      pending = {};
      if (Object.keys(patch).length === 0) return;
      prefsService.save(userId, patch).catch((err) => {
        console.warn("Preferences did not sync:", err);
      });
    };

    const queue = (patch: Partial<UserPrefs>) => {
      Object.assign(pending, patch);
      if (timer) clearTimeout(timer);
      timer = setTimeout(flush, WRITE_DEBOUNCE_MS);
    };

    const unsubscribeStore = useUIStore.subscribe((state) => {
      // Until the first snapshot lands we don't know which side is newer;
      // hydration below resolves that and seeds from local when there is
      // nothing remote, so an early change is not lost.
      if (remote === null) return;
      const patch = diffPrefs(remote, pickPrefs(state));
      if (Object.keys(patch).length === 0) return;
      remote = { ...remote, ...patch };
      queue(patch);
    });

    const unsubscribeRemote = prefsService.subscribe(
      userId,
      (data) => {
        if (data === null) {
          // First sign-in anywhere: this browser's preferences become the
          // account's, so an existing user keeps their theme and layout.
          const local = pickPrefs(useUIStore.getState());
          remote = local;
          prefsService.save(userId, local).catch((err) => {
            console.warn("Preferences did not sync:", err);
          });
          return;
        }
        const clean = sanitizePrefs(data);
        remote = { ...pickPrefs(useUIStore.getState()), ...clean };
        useUIStore.setState(clean);
      },
      (err) => {
        console.warn("Preferences subscription failed:", err);
      }
    );

    return () => {
      unsubscribeStore();
      unsubscribeRemote();
      if (timer) {
        clearTimeout(timer);
        flush();
      }
    };
  }, [userId]);
}
